import {PredictionResponse, RulPredictionResponse} from '@mira/api';
import {
  Anchor,
  CarDiagram,
  CarPartModal,
  MainBadge,
  MiraIcon,
  VehicleIdentity,
  WithSidebarLayout,
} from '@mira/ui';
import {Box, Text, VStack} from 'platform/foundation-tw';
import {match, Pattern} from 'ts-pattern';

import {useTranslation} from 'react-i18next';

import {chain, countBy, isNil, isNotNil, pipe} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {REPORT_WINDOW_KEY} from '../../constants/reportWindow';
import {getSectionElementId} from '../../constants/sectionIds';
import {useCarDiagramData} from '../../hooks/useCarDiagramData';
import {usePartDetailData} from '../../hooks/usePartDetailData';
import {useScrollSpy} from '../../hooks/useScrollSpy';
import {useTransformedPredictions} from '../../hooks/useTransformedPredictions';
import type {MainCategoryGroupId, TransformedPredictionData} from '../../schemas';
import {getPredictionErrorMessage} from '../../utils/getPredictionErrorMessage';
import {Banner} from '../Banner/Banner';
import {List} from '../List';

const getSeverityCounts = pipe(
  (data: TransformedPredictionData) => data.mainGroups,
  chain((group) => group.subcategories),
  chain((sub) => sub.categories),
  countBy((cat) => cat.rowType),
  (counts: Record<string, number>) => ({
    damages: counts['damage'] ?? 0,
    service: counts['service'] ?? 0,
    suggestions: counts['suggestions'] ?? 0,
  })
);

function scrollToSection(groupId: MainCategoryGroupId) {
  const element = document.getElementById(getSectionElementId(groupId));
  if (isNil(element)) {
    return;
  }
  element.scrollIntoView({behavior: 'smooth', block: 'start'});
}

interface PredictionErrorBannerProps {
  error: unknown;
}

function PredictionErrorBanner(props: PredictionErrorBannerProps) {
  const message = getPredictionErrorMessage(props.error);

  return (
    <Banner
      tone="warning"
      title={message.title}
      description={message.description}
      testId="predictionErrorBanner"
    />
  );
}

interface PredictionReportProps {
  vin: string;
  predictionData: PredictionResponse | undefined;
  rulData?: RulPredictionResponse | null;
  isLoading: boolean;
  error: unknown;
  selectedPartId: string | null;
  onPartClick: (partId: string | null) => void;
  /** Make code (e.g. `MAKE_SKODA`) used for brand logo resolution. Provided when opened from DMS. */
  make?: string;
  /** Localized make label (e.g. `Škoda`). Provided when opened from DMS. */
  makeLabel?: string;
  /** Composed model title (e.g. `Superb Laurin & Klement`). Provided when opened from DMS. */
  modelTitle?: string;
}

export function PredictionReport(props: PredictionReportProps) {
  // `useTranslation()` participates in i18next's suspense — guarantees the
  // tree only renders once translations are ready, so synchronous `i18n.t`
  // calls deeper in the transform pipeline resolve correctly.
  const {t} = useTranslation();
  const rulData = isNotNil(props.predictionData) ? props.rulData : undefined;

  const transformedData = useTransformedPredictions(props.predictionData, {
    windowKey: REPORT_WINDOW_KEY,
    filterByWillNeed: false,
    rulResponse: rulData,
  });

  const carDiagramData = useCarDiagramData(props.predictionData, REPORT_WINDOW_KEY);
  const severityCounts = getSeverityCounts(transformedData);
  const selectedPartDetail = usePartDetailData(props.selectedPartId, transformedData);

  const sectionIds = transformedData.mainGroups.map((group) => getSectionElementId(group.groupId));
  const {activeId: activeSectionId, lockActive} = useScrollSpy(sectionIds);

  const hasVehicleIdentityText =
    isNotNilOrEmpty(props.makeLabel) || isNotNilOrEmpty(props.modelTitle);

  return (
    <Box maxWidth="108rem" center minHeight="100dvh">
      <Box paddingHorizontal={8} paddingTop={2}>
        <VStack>
          <div className="relative flex items-center px-6 py-4">
            {hasVehicleIdentityText ? (
              <VehicleIdentity
                make={props.make}
                makeLabel={props.makeLabel}
                modelTitle={props.modelTitle}
              />
            ) : (
              <span className="text-lg font-semibold text-(--color-text-primary) truncate">
                {props.vin}
              </span>
            )}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-3 pointer-events-auto">
                {severityCounts.damages > 0 ? (
                  <MainBadge type="damage" count={severityCounts.damages} />
                ) : null}
                {severityCounts.service > 0 ? (
                  <MainBadge type="service" count={severityCounts.service} />
                ) : null}
                {severityCounts.suggestions > 0 ? (
                  <MainBadge type="suggestions" count={severityCounts.suggestions} />
                ) : null}
              </div>
            </div>
          </div>
          <Box paddingTop={4}>
            <div className="relative overflow-x-hidden">
              <CarDiagram data={carDiagramData} onPartClick={props.onPartClick} />
              <CarPartModal data={selectedPartDetail} onClose={() => props.onPartClick(null)} />
            </div>
          </Box>
          <Box paddingBottom={8}>
            <WithSidebarLayout>
              <WithSidebarLayout.Sidebar>
                <nav aria-label={t('predictionReport.sectionsAriaLabel')}>
                  <VStack spacing={2}>
                    {transformedData.mainGroups.map((mainGroup) => {
                      const sectionId = getSectionElementId(mainGroup.groupId);
                      const handleAnchorClick = () => {
                        lockActive(sectionId);
                        scrollToSection(mainGroup.groupId);
                      };
                      return (
                        <Anchor
                          key={mainGroup.groupId}
                          label={mainGroup.title}
                          icon={<MiraIcon name={mainGroup.icon} />}
                          href={sectionId}
                          state={activeSectionId === sectionId ? 'active' : 'idle'}
                          onClick={handleAnchorClick}
                        />
                      );
                    })}
                  </VStack>
                </nav>
              </WithSidebarLayout.Sidebar>
              <WithSidebarLayout.Content>
                <Box padding={8}>
                  {match({isLoading: props.isLoading, error: props.error})
                    .with({isLoading: true}, () => <Text>{t('predictionReport.loading')}</Text>)
                    .with({error: Pattern.nonNullable}, ({error}) => (
                      <PredictionErrorBanner error={error} />
                    ))
                    .otherwise(() => (
                      <List mainGroups={transformedData.mainGroups} />
                    ))}
                </Box>
              </WithSidebarLayout.Content>
            </WithSidebarLayout>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}
